// Tone math (§6.4), CPU reference. The GLSL passes implement exactly these formulas; golden tests
// compare the GPU output against this module.

export const smoothstep = (e0: number, e1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};
export const mix = (a: number, b: number, t: number) => a + (b - a) * t;

export type ToneParams = { highlights: number; shadows: number; contrast: number };

/**
 * New luminance for a pixel of luminance `Y` whose low-res guide luminance is `guide` (§6.4).
 * RGB is then scaled by Y'/Y (with Y floored at 1e-6) to preserve hue.
 */
export function toneY(Y: number, guide: number, p: ToneParams): number {
  if (p.highlights === 0 && p.shadows === 0 && p.contrast === 0) return Y;
  const y = Math.max(Y, 1e-6);
  const L = Math.log2(y / 0.18);
  const Lb = Math.log2(Math.max(guide, 1e-6) / 0.18);
  const Lm = mix(L, Lb, 0.6);
  let dL = 0;
  dL += (p.shadows / 100) * 1.5 * (1 - smoothstep(-6, 0, Lm));
  dL += (p.highlights / 100) * 1.5 * smoothstep(0, 3, Lm);
  const L2 = (L + dL) * (1 + (p.contrast / 100) * 0.5);
  return 0.18 * Math.pow(2, L2);
}

/** Pass 7 highlight shoulder: soft roll-off above 0.85 (always on). */
export function shoulder(x: number): number {
  return x > 0.85 ? 0.85 + 0.15 * (1 - Math.exp(-(x - 0.85) / 0.15)) : x;
}

/** Whites / blacks on encoded value v (§6.4); clamped to 0..1 afterwards (curve stage only). */
export function whitesBlacks(v: number, whites: number, blacks: number): number {
  let o = v + (whites / 100) * 0.2 * v ** 4;
  o += (blacks / 100) * 0.12 * (1 - v) ** 4;
  return Math.min(1, Math.max(0, o));
}

export const exposureGain = (ev: number) => Math.pow(2, ev);
